import { useEffect, useState } from "react";
import { api, dollars, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Trash2, UserCog, Shield, Star, Flame, BadgeCheck } from "lucide-react";
import GrowthChannelsPanel from "@/components/GrowthChannelsPanel";
import ReleasesPanel from "@/components/ReleasesPanel";

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [ffActive, setFfActive] = useState(false);
  const [mmActive, setMmActive] = useState(false);

  const load = async () => {
    const [s, u, t, a, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/tracks"),
      api.get("/albums?limit=100"), api.get("/config/public"),
    ]);
    setStats(s.data); setUsers(u.data.users); setTracks(t.data.tracks);
    setAlbums(a.data.albums); setFfActive(!!c.data.forge_friday_active); setMmActive(!!c.data.melo_monday_active);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div className="p-16 text-center font-mono text-xs uppercase text-gray-500">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== "admin") return <Navigate to="/" />;

  const del = async (id) => {
    if (!window.confirm("Soft-delete this track?")) return;
    await api.delete(`/admin/tracks/${id}`); toast.success("Track removed"); load();
  };
  const toggleArtist = async (id) => { await api.put(`/admin/users/${id}/toggle-artist`); load(); };
  const toggleVerify = async (id) => {
    try { const { data } = await api.put(`/admin/users/${id}/verify`);
      toast.success(data.verified ? "Marked verified" : "Verification removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setFeatured = async (albumId) => {
    try { await api.put(`/admin/albums/${albumId}/feature`); toast.success("Featured on homepage"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const clearFeatured = async () => { await api.delete(`/admin/featured/clear`); toast.success("Featured slot cleared"); load(); };
  const toggleFF = async (v) => {
    try { const { data } = await api.put(`/admin/forge-friday/${v ? "on" : "off"}`);
      setFfActive(!!data.forge_friday_active); toast.success(`MeloCraft Friday ${v ? "activated" : "off"}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleMM = async (v) => {
    try { const { data } = await api.put(`/admin/melo-monday/${v ? "on" : "off"}`);
      setMmActive(!!data.melo_monday_active); toast.success(`Melo Mondays ${v ? "activated" : "off"}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const featured = albums.find((a) => a.featured);

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 pb-32">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="h-6 w-6 text-[#FF4500]" />
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500]">Admin console</div>
      </div>
      <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-10">Full control.</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10">
          {[
            ["users", stats.users], ["artists", stats.artists],
            ["tracks", stats.tracks], ["albums", stats.albums],
            ["sales", stats.purchases], ["revenue", dollars(stats.revenue_cents || 0)],
          ].map(([k, v]) => (
            <div key={k} className="border border-[#2A2A2A] bg-[#121212] p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{k}</div>
              <div className="font-display text-2xl md:text-3xl mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Global controls */}
      <GrowthChannelsPanel />
      <ReleasesPanel />
      <div className="grid md:grid-cols-3 gap-4 mb-10 mt-6">
        <div className="border border-[#2A2A2A] bg-[#121212] p-5 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-display text-lg"><Flame className="h-5 w-5 text-[#FF4500]" /> Melo Mondays</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
              Auto every Monday (US Eastern) · Toggle expires at midnight
            </div>
          </div>
          <Switch data-testid="mm-toggle" checked={mmActive} onCheckedChange={toggleMM} />
        </div>
        <div className="border border-[#2A2A2A] bg-[#121212] p-5 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-display text-lg"><Flame className="h-5 w-5 text-[#FF4500]" /> MeloCraft Friday</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">Site-wide banner · 100% to artists</div>
          </div>
          <Switch data-testid="ff-toggle" checked={ffActive} onCheckedChange={toggleFF} />
        </div>
        <div className="border border-[#2A2A2A] bg-[#121212] p-5">
          <div className="flex items-center gap-2 font-display text-lg"><Star className="h-5 w-5 text-[#FF4500]" /> Featured drop</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1 mb-3">
            {featured ? `Now featuring: ${featured.title} — ${featured.artist_name}` : "No album currently featured"}
          </div>
          {featured && <Button data-testid="clear-featured" size="sm" variant="outline"
                               onClick={clearFeatured}
                               className="rounded-none border-[#2A2A2A] hover:border-red-500 hover:text-red-500 bg-transparent font-mono text-[10px] uppercase h-8">
            Clear featured slot
          </Button>}
        </div>
        <div className="border border-[#2A2A2A] bg-[#121212] p-5">
          <div className="flex items-center gap-2 font-display text-lg">📬 Weekly digest</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1 mb-3">
            Email every fan with top 5 drops from artists they follow this week
          </div>
          <Button data-testid="send-digest" size="sm" onClick={async () => {
            try { const { data } = await api.post("/admin/send-weekly-digest");
              toast.success(`Digest sent · ${data.emails_sent} emails`); }
            catch (e) { toast.error(errMsg(e)); }
          }}
                  className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-[10px] uppercase h-8 px-3">
            Send digest now
          </Button>
        </div>
        <div className="border border-[#2A2A2A] bg-[#121212] p-5">
          <div className="flex items-center gap-2 font-display text-lg">🖼️ Backfill posters</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1 mb-3">
            Auto-generate share posters (1080×1920) for every existing track that doesn't have one yet
          </div>
          <Button data-testid="backfill-posters" size="sm" onClick={async () => {
            try {
              toast.info("Generating posters — this can take a moment…");
              const { data } = await api.post("/admin/posters/backfill");
              toast.success(`Posters generated · ${data.generated}/${data.scanned}${data.failed?.length ? ` · ${data.failed.length} failed` : ""}`);
            } catch (e) { toast.error(errMsg(e)); }
          }}
                  className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-[10px] uppercase h-8 px-3">
            Generate now
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-10">
        <div>
          <h2 className="font-display text-2xl mb-3">Users ({users.length})</h2>
          <div className="border border-[#2A2A2A] max-h-[500px] overflow-y-auto divide-y divide-[#2A2A2A]">
            {users.map((u) => (
              <div key={u.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="font-display truncate flex items-center gap-1.5">
                    {u.display_name || u.username}
                    {u.verified && <BadgeCheck className="h-3.5 w-3.5 text-[#FF4500]" />}
                    <span className="text-gray-500 text-xs">@{u.username}</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 truncate">
                    {u.email} · {u.role} {u.subscription_active && <span className="text-[#FF4500]">· PAID</span>}
                  </div>
                </div>
                {u.is_artist && (
                  <Button data-testid={`verify-${u.id}`} size="sm" variant="outline"
                          onClick={() => toggleVerify(u.id)}
                          className={`rounded-none bg-transparent font-mono text-[10px] uppercase h-8 ${u.verified ? "border-[#FF4500] text-[#FF4500]" : "border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500]"}`}>
                    <BadgeCheck className="h-3 w-3 mr-1" /> {u.verified ? "Verified" : "Verify"}
                  </Button>
                )}
                <Button data-testid={`toggle-artist-${u.id}`} size="sm" variant="outline"
                        onClick={() => toggleArtist(u.id)}
                        className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-[10px] uppercase h-8">
                  <UserCog className="h-3 w-3 mr-1" /> {u.is_artist ? "Demote" : "Make artist"}
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-2xl mb-3">Tracks ({tracks.length})</h2>
          <div className="border border-[#2A2A2A] max-h-[500px] overflow-y-auto divide-y divide-[#2A2A2A]">
            {tracks.map((t) => (
              <div key={t.id} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className={`font-display truncate ${t.deleted ? "line-through text-gray-600" : ""}`}>{t.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 truncate">
                    {t.artist_name} · {t.plays || 0} plays · {t.sales || 0} sales
                  </div>
                </div>
                {!t.deleted && (
                  <Button data-testid={`del-track-${t.id}`} size="sm" variant="outline" onClick={() => del(t.id)}
                          className="rounded-none border-[#2A2A2A] hover:border-red-500 hover:text-red-500 bg-transparent h-8">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Albums moderation + Feature */}
      <div>
        <h2 className="font-display text-2xl mb-3">Albums ({albums.length})</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {albums.map((a) => (
            <div key={a.id} className={`border ${a.featured ? "border-[#FF4500]" : "border-[#2A2A2A]"} bg-[#121212]`}>
              <div className="aspect-square bg-neutral-900 relative">
                {a.cover_path && <img src={fileUrl(a.cover_path)} alt={a.title} className="h-full w-full object-cover" />}
                {a.featured && <div className="absolute top-2 left-2 bg-[#FF4500] text-black px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest">Featured</div>}
              </div>
              <div className="p-3">
                <div className="font-display truncate">{a.title}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 truncate">{a.artist_name}</div>
                <Button data-testid={`feature-${a.id}`} size="sm" variant="outline" onClick={() => setFeatured(a.id)}
                        disabled={a.featured}
                        className="mt-2 w-full rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-[10px] uppercase h-8">
                  {a.featured ? "Featured" : "Feature this"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
